import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useILScore } from '@/hooks/useILScore';
import { useMentalWellnessScore } from '@/hooks/useMentalWellnessScore';
import { Activity, Brain, TrendingUp, TrendingDown, Minus, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function MyScoresDashboard() {
  const navigate = useNavigate();
  const { todayScore: ilScore, sevenDayAvg: ilAvg, isLoading: ilLoading } = useILScore(7);
  const { todayScore: mwScore, sevenDayAvg: mwAvg, isLoading: mwLoading } = useMentalWellnessScore(7);

  const getILZone = (score: number) => {
    if (score < 34) return { label: 'Green', color: 'bg-accent', text: 'text-accent' };
    if (score < 67) return { label: 'Amber', color: 'bg-secondary', text: 'text-secondary' };
    return { label: 'Red', color: 'bg-destructive', text: 'text-destructive' };
  };

  const getMWZone = (score: number) => {
    if (score >= 70) return { label: 'Thriving', color: 'bg-accent', text: 'text-accent' };
    if (score >= 50) return { label: 'Managing', color: 'bg-primary', text: 'text-primary' };
    if (score >= 30) return { label: 'Struggling', color: 'bg-secondary', text: 'text-secondary' };
    return { label: 'Crisis', color: 'bg-destructive', text: 'text-destructive' };
  };

  const getTrend = (current?: number, avg?: number) => {
    if (!current || !avg) return null;
    const diff = current - avg;
    if (Math.abs(diff) < 5) return { icon: Minus, label: 'Stable', color: 'text-muted-foreground' };
    if (diff > 0) return { icon: TrendingUp, label: '+' + Math.round(diff), color: 'text-accent' };
    return { icon: TrendingDown, label: Math.round(diff).toString(), color: 'text-destructive' };
  };

  const ilZone = ilScore ? getILZone(ilScore.il_score) : null;
  const mwZone = mwScore ? getMWZone(mwScore.mental_wellness_score) : null;
  const ilTrend = getTrend(ilScore?.il_score, ilAvg);
  const mwTrend = getTrend(mwScore?.mental_wellness_score, mwAvg);

  if (ilLoading || mwLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-6 animate-pulse">
          <div className="h-24 bg-muted rounded" />
        </Card>
        <Card className="p-6 animate-pulse">
          <div className="h-24 bg-muted rounded" />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-2">My Health Scores</h2>
        <p className="text-muted-foreground">
          Your body and mind wellness at a glance
        </p>
      </div>

      {/* IL Score Card */}
      <Card className="p-6 bg-gradient-to-br from-secondary/10 to-primary/10 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/inflammation')}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-gradient-to-br from-secondary/20 to-primary/20">
              <Activity className="w-6 h-6 text-secondary" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Inflammation Load (IL)</h3>
              <p className="text-sm text-muted-foreground">Physical health factors</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>

        {ilScore ? (
          <>
            <div className="flex items-end gap-3 mb-4">
              <div className="text-5xl font-bold">{Math.round(ilScore.il_score)}</div>
              <div className={`text-2xl font-semibold ${ilZone?.text} mb-2`}>
                {ilZone?.label}
              </div>
              {ilTrend && (
                <div className={`flex items-center gap-1 ${ilTrend.color} mb-2`}>
                  <ilTrend.icon className="w-4 h-4" />
                  <span className="text-sm font-semibold">{ilTrend.label}</span>
                </div>
              )}
            </div>

            <Progress value={ilScore.il_score} className="h-3 mb-4" />

            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-muted-foreground">7-Day Avg</div>
                <div className="font-semibold">{ilAvg ? Math.round(ilAvg) : '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Top Factor</div>
                <div className="font-semibold capitalize">{ilScore.contributors?.[0] || '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div className={`font-semibold ${ilZone?.text}`}>{ilZone?.label}</div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">
              Complete your Daily Check-In to see your IL Score
            </p>
            <Button onClick={() => navigate('/daily-check-in')}>
              Start Check-In
            </Button>
          </div>
        )}
      </Card>

      {/* Mental Wellness Score Card */}
      <Card className="p-6 bg-gradient-to-br from-accent/10 to-primary/10 hover:shadow-lg transition-all cursor-pointer"
            onClick={() => navigate('/mental-wellness')}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-gradient-to-br from-accent/20 to-primary/20">
              <Brain className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Mental Wellness Score</h3>
              <p className="text-sm text-muted-foreground">Emotional & psychological health</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground" />
        </div>

        {mwScore ? (
          <>
            <div className="flex items-end gap-3 mb-4">
              <div className="text-5xl font-bold">{Math.round(mwScore.mental_wellness_score)}</div>
              <div className={`text-2xl font-semibold ${mwZone?.text} mb-2`}>
                {mwZone?.label}
              </div>
              {mwTrend && (
                <div className={`flex items-center gap-1 ${mwTrend.color} mb-2`}>
                  <mwTrend.icon className="w-4 h-4" />
                  <span className="text-sm font-semibold">{mwTrend.label}</span>
                </div>
              )}
            </div>

            <Progress value={mwScore.mental_wellness_score} className="h-3 mb-4" />

            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-muted-foreground">7-Day Avg</div>
                <div className="font-semibold">{mwAvg ? Math.round(mwAvg) : '-'}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Mood</div>
                <div className="font-semibold">{mwScore.mood_rating}/10</div>
              </div>
              <div>
                <div className="text-muted-foreground">Status</div>
                <div className={`font-semibold ${mwZone?.text}`}>{mwZone?.label}</div>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-6">
            <p className="text-muted-foreground mb-4">
              Complete your Evening Mental Health Check-In to see your score
            </p>
            <Button onClick={() => navigate('/mental-wellness')}>
              Start Evening Check-In
            </Button>
          </div>
        )}
      </Card>

      {/* Combined Insight */}
      {ilScore && mwScore && (
        <Card className="p-6 bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
          <h4 className="font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Holistic Health Insight
          </h4>
          <p className="text-sm text-muted-foreground">
            {generateInsight(ilScore.il_score, mwScore.mental_wellness_score, ilScore.contributors)}
          </p>
          <Button 
            variant="outline" 
            className="w-full mt-4"
            onClick={() => navigate('/therapy')}
          >
            Talk to Therappi About Your Scores
          </Button>
        </Card>
      )}
    </div>
  );
}

function generateInsight(ilScore: number, mwScore: number, ilContributors: string[]): string {
  const ilZone = ilScore < 34 ? 'green' : ilScore < 67 ? 'amber' : 'red';
  const mwZone = mwScore >= 70 ? 'thriving' : mwScore >= 50 ? 'managing' : mwScore >= 30 ? 'struggling' : 'crisis';

  if (ilZone === 'red' && mwZone === 'crisis') {
    return "Both your physical inflammation and mental wellness need immediate attention. Consider starting with stress management and sleep optimisation, as these affect both scores. Therappi can help create a comprehensive recovery plan.";
  }

  if (ilZone === 'green' && mwZone === 'thriving') {
    return "Excellent! Both your physical health and mental wellness are strong. This is the ideal state for longevity and quality of life. Keep up your healthy habits and consider exploring advanced optimisation protocols.";
  }

  if (ilScore > mwScore) {
    const topFactor = ilContributors[0];
    return `Your inflammation score is higher than your mental wellness score, driven mainly by ${topFactor}. Physical health challenges can impact mood and energy. Focus on ${topFactor} management first - improvements here often boost mental wellness too.`;
  }

  return `Your mental wellness needs more support than your physical health. Emotional stress, poor sleep, and anxiety can drive inflammation over time. Prioritise mental health tools in The Sanctuary and evening reflection practices.`;
}
